import { useTranslation } from "react-i18next";

import { useGetServiceTemplate } from "api/services/appointment/hooks";
import type { ServiceTemplateField } from "api/services/appointment/schemas/hcp_service";

import { END_TIME, START_TIME } from "../helpers/misc";
import { convertMinutesToTime, generateArrayWithMinutesStep } from "../helpers/settingsHelpers";

type Option = {
  label: string;
  value: string | number;
};

export const useServiceFormOptions = (managerId: number) => {
  const { t } = useTranslation();
  const { data: serviceTemplate } = useGetServiceTemplate(managerId);

  const generateOptions = (field: ServiceTemplateField, key?: string): Option[] => {
    if (serviceTemplate === undefined) return [];

    if (key) {
      return (
        serviceTemplate[field].map(option => {
          // FIXME: type translation
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          return { label: `${option} ${t(`booking.services.${key}`)}`, value: option };
        }) ?? []
      );
    }
    return (
      serviceTemplate[field].map(option => {
        // FIXME: type translation
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        return { label: t(`booking.services.${field}_options.${option}`) as string, value: option };
      }) ?? []
    );
  };

  const optionsWorkHours = generateArrayWithMinutesStep(START_TIME, END_TIME).map(option => {
    return { value: option, label: convertMinutesToTime(option) };
  });

  const optionsNoEarlierThan = [
    { label: `1 ${t("common.hour")}`.toLowerCase(), value: 1 * 60 },
    { label: `2 ${t("common.hours")}`.toLowerCase(), value: 2 * 60 },
    { label: `3 ${t("common.hours")}`.toLowerCase(), value: 3 * 60 },
    { label: `4 ${t("common.hours")}`.toLowerCase(), value: 4 * 60 },
  ];

  const optionsNoLaterThan = [
    { label: `3 ${t("common.days")}`.toLowerCase(), value: 3 * 24 * 60 },
    { label: `7 ${t("common.days")}`.toLowerCase(), value: 7 * 24 * 60 },
    { label: `14 ${t("common.days")}`.toLowerCase(), value: 14 * 24 * 60 },
    { label: `28 ${t("common.days")}`.toLowerCase(), value: 28 * 24 * 60 },
  ];

  const optionsHealthQuestionnaireType = [
    { label: t("booking.services.health_questionnaire_type_options.default"), value: "default" },
    { label: t("booking.services.health_questionnaire_type_options.none"), value: "none" },
  ];

  const optionsHealthCareProfessionals =
    serviceTemplate?.health_care_professional_ids.map(option => {
      return { label: option.full_name, value: option.id.toString() };
    }) ?? [];

  const optionsCategory =
    serviceTemplate?.health_care_provider_service_categories.map(option => {
      return { label: option.name, value: option.id };
    }) ?? [];

  const optionsPurpose = generateOptions("purpose");
  const optionsDuration = generateOptions("duration", "minutes");
  const optionsMode = generateOptions("mode");
  const optionsPaymentMethods = generateOptions("payment_methods");

  return {
    serviceTemplate,
    optionsWorkHours,
    optionsNoEarlierThan,
    optionsNoLaterThan,
    optionsHealthQuestionnaireType,
    optionsHealthCareProfessionals,
    optionsCategory,
    optionsPurpose,
    optionsDuration,
    optionsMode,
    optionsPaymentMethods,
  };
};
